import React, { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { useRef, useEffect} from "react";

export default function Shop({
  product,
  selectedSize,
  setSelectedSize,
  quantity,
  setQuantity,
  addToCart,
  openCheckoutForm,
}) {
    
  const videoRefs = useRef([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? slidingMedia.length - 1 : prev - 1,
    );
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === slidingMedia.length - 1 ? 0 : prev + 1,
    );
  };

  const slidingMedia = [
    ...product.imagesShop.slice(0, -1),
    ...product.prodVideos.map((video) => ({
      ...video,
      type: "video",
    })),
  ];

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;

      if (index === currentImageIndex) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [currentImageIndex]);

  const collagePhoto = product.imagesShop[product.imagesShop.length - 1];

  return (
    <div className="pt-20 pb=5 px-6 sm:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Left side - Image Gallery (appears second on mobile) */}
          <div className="relative h-fit order-2 md:order-1">
            <div className="relative flex flex-col gap-8">
              {/* Main Image with Navigation */}
              <div className="relative flex justify-center overflow-hidden">
                <button
                  onClick={prevImage}
                  className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow p-3 rounded-full transition z-10"
                >
                  ‹
                </button>

                <div
                  className="w-full flex transition-transform duration-500 ease-in-out"
                  style={{
                    transform: `translateX(-${currentImageIndex * 100}%)`,
                  }}
                >
                  {slidingMedia.map((media, idx) => (
                    <div
                      key={idx}
                      className="w-full flex-shrink-0 flex justify-center"
                    >
                      {media.type === "video" ? (
                        <video
                          ref={(el) => (videoRefs.current[idx] = el)}
                          src={media.src}
                          className={media.width}
                          muted
                          loop
                          playsInline
                          controls
                        />
                      ) : (
                        <img
                          src={media.src}
                          alt={`View ${idx + 1}`}
                          className={media.width}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <button
                  onClick={nextImage}
                  className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow p-3 rounded-full transition z-10"
                >
                  ›
                </button>
              </div>

              {/* Fixed Bottom Image */}
              <img
                src={collagePhoto.src}
                alt="Product collage"
                className={`${collagePhoto.width} mx-auto my-10 scale-120 md:scale-100`}
              />
            </div>
          </div>

          {/* Right side - Product Details (appears first on mobile) */}
          <div className="md:sticky md:top-32 h-fit order-1 md:order-2">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight">
              ZipNZip
              <br />
              Joggers
            </h2>
            <div className="mb-8">
              <p className="text-3xl font-semibold text-black mt-[35px]">
                ₹1,999
              </p>

              <div className="flex items-center gap-2">
                <span className="text-gray-500 line-through">₹3,000</span>
                <span className="text-green-600 font-medium">33% OFF</span>
              </div>
            </div>
            <div className="mb-8">
              <label className="block text-sm font-medium mb-4">Size</label>
              <div className="flex gap-3">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`w-14 h-14 border text-sm font-medium transition ${
                      selectedSize === size
                        ? "border-black bg-black text-white"
                        : "border-gray-300 hover:border-black"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-10">
              <label className="block text-sm font-medium mb-4">Quantity</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 border border-gray-300 flex items-center justify-center hover:border-black transition"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span className="text-lg w-12 text-center font-medium">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-12 h-12 border border-gray-300 flex items-center justify-center hover:border-black transition"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            <button
              onClick={addToCart}
              className="w-full bg-black text-white py-4 text-sm font-medium hover:bg-gray-800 transition mb-3"
            >
              Add to Cart
            </button>

            <button
              onClick={() => {
                addToCart();
                setTimeout(openCheckoutForm, 300);
              }}
              className="w-full border border-black py-4 text-sm font-medium hover:bg-gray-50 transition"
            >
              Buy Now
            </button>

            <div className="mt-5 pt-12 border-t border-gray-200">
              <h3 className="text-sm font-medium mb-6">Product Details</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>• 91% premium polyester + 9% spandex stretchable fabric</li>
                <li>• Dual side zippers (ankle to waist)</li>
                <li>• Two front pockets</li>
                <li>• Machine washable</li>
                <li>• Made in India</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
